import { useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Search, X } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { isPanicTile } from '@shared/lib/tileActions';
import { ListItem } from '@shared/ui/lists/ListItem';
import { QuickLinkIcon } from '@features/dashboard/QuickLinkTile';
import { listHeadingClass } from '@features/dashboard/widgets/WidgetCard';
import type { CompiledPublicTile } from '@shared/types/domainConfig';

export interface ExploreGroup {
  id: string;
  /** Empty for the organisation's main tiles, which carry no group heading. */
  name: string;
  tiles: CompiledPublicTile[];
}

const LAST_USED_COUNT = 5;

/**
 * Spotlight-style search for Explore: a floating box over the blurred page.
 * Empty, it lists the features the member used last (if any); typing lists every matching feature
 * once, with its group. Enter opens the first result; Escape or a tap outside
 * closes.
 */
export function ExploreSearch({
  open,
  onOpenChange,
  groups,
  recent,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: ExploreGroup[];
  /** Feature ids, most recently opened first. */
  recent: string[];
  onSelect: (tile: CompiledPublicTile) => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // 1. Every feature once, with the first group it appears in.
  const all = new Map<string, { tile: CompiledPublicTile; group: string }>();
  for (const group of groups) {
    for (const tile of group.tiles) {
      if (!all.has(tile.id)) all.set(tile.id, { tile, group: group.name });
    }
  }

  // 2. Typing matches labels; empty shows the last used.
  const q = query.trim().toLowerCase();
  const results = q
    ? [...all.values()].filter(({ tile }) => tile.label.toLowerCase().includes(q))
    : recent
        .filter((id) => all.has(id))
        .slice(0, LAST_USED_COUNT)
        .map((id) => ({ ...all.get(id)!, group: '' }));

  const close = (next: boolean) => {
    if (!next) setQuery('');
    onOpenChange(next);
  };

  const select = (tile: CompiledPublicTile) => {
    close(false);
    onSelect(tile);
  };

  return (
    <Dialog.Root open={open} onOpenChange={close}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-md" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed inset-x-4 top-[22dvh] z-[60] overflow-hidden rounded-[22px] bg-[color-mix(in_oklch,var(--surface-card)_95%,transparent)] shadow-[0_24px_60px_-16px_rgba(0,0,0,0.45)] outline-none backdrop-blur-xl"
        >
          <Dialog.Title className="sr-only">Search</Dialog.Title>

          {/* Search field */}
          <label className="flex h-[3.625rem] items-center gap-3 px-4">
            <Search className="h-5 w-5 shrink-0 text-ink-muted" strokeWidth={2} />
            <span className="sr-only">Search</span>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && results[0]) select(results[0].tile);
              }}
              placeholder="Search"
              autoComplete="off"
              enterKeyHint="go"
              className="min-w-0 flex-1 bg-transparent text-[1.125rem] text-ink outline-none placeholder:text-ink-muted"
            />
            {query ? (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => {
                  setQuery('');
                  inputRef.current?.focus();
                }}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-pill bg-hairline-strong text-white"
              >
                <X className="h-3 w-3" strokeWidth={3} />
              </button>
            ) : null}
          </label>

          {/* Results — nothing below the field until there's something to show. */}
          {q || results.length > 0 ? (
            <div className="max-h-[min(21rem,40dvh)] overflow-y-auto border-t border-hairline px-4 pb-2 pt-1">
              {q ? null : <p className={cn(listHeadingClass, 'mb-0.5 mt-2.5 px-0')}>Last used</p>}

              {results.length > 0 ? (
                <ul className="divide-y divide-hairline">
                  {results.map(({ tile, group }) => (
                    <li key={tile.id}>
                      <ListItem
                        title={tile.label}
                        leading={
                          <QuickLinkIcon
                            link={{ label: tile.label, imageUrl: tile.image_url, isPanic: isPanicTile(tile) }}
                            className="h-[2.125rem] w-[2.125rem]"
                          />
                        }
                        trailing={group ? <span className="text-body-sm text-ink-muted">{group}</span> : undefined}
                        noChevron
                        onClick={() => select(tile)}
                        className="px-0 py-2"
                      />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="pb-4 pt-5 text-center text-body-sm text-ink-secondary">Nothing found</p>
              )}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
